import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../NavBar/Navbar';
import Table from '../Table/Table';
import FormSmartpayModal from '../Modal/Modal';
import { BranchOfficeFetchAPI } from '../../api/BranchOffices';
import { TablesFetchAPI } from '../../api/Tables';
import { ClientsFetchAPI } from '../../api/Clients';
import { BranchOfficeEnvironmentsFetchAPI } from '../../api/BranchOfficeEnvironments';
import ErrorAlert from '../Alerts/ErrorAlert';
import { RenderIf } from '../utils/RenderIf';
import ModalBranchofficeSelect from '../NavBar/ModalBranchofficeSelect';
import { useSelectedBranch } from '../../../context/BranchContext';
import { generateRandomString } from '../utils/RandomString';
import Swal from 'sweetalert2';
import AppShell from '../../page/AppShell/AppShell';
import MainContent from '../MainContent/MainContent';
import { TableSessionsFetchAPI } from '../../api/TableSessions';
import TableAccessQrModal from './TableAccessQrModal';
import PrepaySettingsModal from './PrepaySettingsModal';

/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo: .rt-
───────────────────────────────────────────────────────── */
const RT_CSS = `
@keyframes rt-up   { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
@keyframes rt-fade { from{opacity:0} to{opacity:1} }
@keyframes rt-pop  { 0%,100%{transform:scale(1)} 40%{transform:scale(1.08)} 70%{transform:scale(.96)} }

/* ── page ── */
.rt-page {
  min-height: 100vh;
  background: var(--bg-3, #F8F8F8);
  padding: 1.5rem clamp(1rem,3vw,2rem) 4rem;
}

/* ── banner ── */
.rt-banner {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem;
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px;
  padding: 1.1rem 1.35rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,.04);
}
.rt-banner-left {}
.rt-banner-eyebrow {
  font-size: .68rem; font-weight: 700;
  letter-spacing: .09em; text-transform: uppercase;
  color: var(--brand, #F26522); margin-bottom: .22rem;
}
.rt-banner-title {
  font-size: 1.1rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  letter-spacing: -.02em; line-height: 1.2;
  display: flex; align-items: center; gap: .5rem; flex-wrap: wrap;
}
.rt-banner-branch {
  color: var(--brand, #F26522);
}
.rt-banner-sub { font-size: .77rem; color: var(--text-3, #B4B4B4); margin-top: .2rem; }

/* ── body grid ── */
.rt-grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1.25rem;
  align-items: start;
}
@media(max-width: 900px) { .rt-grid { grid-template-columns: 1fr; } }

/* ── card ── */
.rt-card {
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,.04);
  animation: rt-up .3s ease both;
}

/* ── card header ── */
.rt-card-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: .85rem 1.1rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
}
.rt-card-title {
  font-size: .86rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  display: flex; align-items: center; gap: .4rem;
}
.rt-count-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 18px; border-radius: 5px;
  background: var(--brand-dim, rgba(242,101,34,.09));
  color: var(--brand, #F26522);
  font-size: .65rem; font-weight: 700; padding: 0 .35rem;
}

/* ── env list ── */
.rt-env-list { display: flex; flex-direction: column; gap: .3rem; padding: .6rem; }
.rt-env-item {
  display: flex; align-items: center; gap: .6rem;
  padding: .6rem .75rem;
  border-radius: 10px; cursor: pointer;
  border: 1.5px solid transparent;
  background: transparent;
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .82rem; font-weight: 500;
  text-align: left; width: 100%;
  transition: all .14s;
}
.rt-env-item:hover { background: var(--bg-2, rgba(0,0,0,.04)); color: var(--text, #0A0A0A); }
.rt-env-item.active {
  background: var(--brand-dim, rgba(242,101,34,.09));
  border-color: rgba(242,101,34,.2);
  color: var(--brand, #F26522);
  font-weight: 600;
}
.rt-env-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  background: var(--border-md, rgba(0,0,0,.12));
  transition: background .14s;
}
.rt-env-item.active .rt-env-dot { background: var(--brand, #F26522); }
.rt-env-empty { font-size: .78rem; color: var(--text-3, #B4B4B4); padding: .75rem .9rem; }

/* ── tables card head ── */
.rt-tables-head {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .6rem;
  padding: .85rem 1.1rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
}

/* ── buttons ── */
.rt-btn {
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .38rem .85rem;
  border-radius: 8px; border: none;
  background: var(--brand, #F26522); color: #fff;
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .78rem; font-weight: 600;
  cursor: pointer; transition: background .14s, transform .14s;
}
.rt-btn:hover   { background: var(--brand-hover, #D4521A); transform: translateY(-1px); }
.rt-btn:active  { transform: translateY(0); }

.rt-btn-ghost {
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .38rem .85rem;
  border-radius: 8px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg-2, rgba(0,0,0,.035));
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .78rem; font-weight: 500;
  cursor: pointer; transition: all .14s;
}
.rt-btn-ghost:hover { border-color: var(--brand, #F26522); color: var(--brand, #F26522); background: var(--brand-dim, rgba(242,101,34,.07)); }

/* ── empty state ── */
.rt-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 3rem 1rem; text-align: center;
  animation: rt-up .3s ease both;
}
.rt-empty-icon {
  width: 58px; height: 58px; border-radius: 50%;
  background: var(--bg-2, rgba(0,0,0,.04));
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  display: grid; place-items: center;
  font-size: 1.5rem; margin-bottom: .7rem;
}
.rt-empty-title { font-size: .86rem; font-weight: 600; color: var(--text, #0A0A0A); margin: 0 0 .25rem; }
.rt-empty-sub   { font-size: .76rem; color: var(--text-3, #B4B4B4); margin: 0; }

/* ── no branch selected ── */
.rt-no-branch {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 280px; gap: 1rem;
  background: var(--bg, #fff);
  border: 1.5px dashed var(--border-md, rgba(0,0,0,.13));
  border-radius: 16px; padding: 2rem;
  text-align: center; animation: rt-up .35s ease both;
}
.rt-no-branch-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: var(--brand-dim, rgba(242,101,34,.09));
  color: var(--brand, #F26522);
  display: grid; place-items: center; font-size: 1.6rem;
}
.rt-no-branch-title { font-size: .92rem; font-weight: 700; color: var(--text, #0A0A0A); }
.rt-no-branch-sub   { font-size: .78rem; color: var(--text-3, #B4B4B4); max-width: 260px; line-height: 1.6; }

/* ── table (desktop) ── */
.rt-table-wrap { overflow-x: auto; scrollbar-width: thin; }
.rt-table-wrap::-webkit-scrollbar { height: 4px; }
.rt-table-wrap::-webkit-scrollbar-thumb { background: rgba(0,0,0,.08); border-radius: 2px; }

/* ── mobile cards grid ── */
.rt-mobile-grid { display: none; flex-direction: column; gap: .5rem; padding: .75rem; }

.rt-table-card {
  display: flex; align-items: center; gap: .75rem;
  padding: .85rem .9rem;
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 12px;
  background: var(--bg, #fff);
  transition: box-shadow .14s, border-color .14s;
  animation: rt-up .2s ease both;
}
.rt-table-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,.07); border-color: var(--border-md, rgba(0,0,0,.11)); }

.rt-table-card-icon {
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  background: var(--brand-dim, rgba(242,101,34,.09));
  color: var(--brand, #F26522);
  display: grid; place-items: center; font-size: 1.1rem;
}
.rt-table-card-body { flex: 1; min-width: 0; }
.rt-table-card-name  { font-size: .84rem; font-weight: 700; color: var(--text, #0A0A0A); }
.rt-table-card-code  { font-size: .7rem; color: var(--text-3, #B4B4B4); margin-top: 2px; }
.rt-table-card-right { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: .3rem; }

.rt-status-dot {
  display: inline-flex; align-items: center; gap: .28rem;
  padding: .18rem .55rem; border-radius: 999px;
  font-size: .66rem; font-weight: 700;
}
.rt-status-open   { background: rgba(34,197,94,.1);  color: #15803D; }
.rt-status-closed { background: rgba(0,0,0,.05);     color: var(--text-3, #B4B4B4); }

/* ── pagination ── */
.rt-pagination {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .75rem;
  padding: .85rem 1.1rem;
  border-top: 1px solid var(--border, rgba(0,0,0,.07));
}
.rt-pag-info { font-size: .75rem; color: var(--text-3, #B4B4B4); font-family: var(--font-sans, -apple-system, sans-serif); }
.rt-pag-info span { color: var(--text-2, #6B6B6B); font-weight: 600; }
.rt-pag-controls { display: flex; align-items: center; gap: .3rem; flex-wrap: wrap; }
.rt-pag-size {
  display: inline-flex; align-items: center; gap: .35rem;
  font-size: .74rem; color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
}
.rt-pag-size select {
  padding: .26rem .5rem;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  border-radius: 7px; background: var(--bg, #fff);
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .74rem; font-weight: 600; outline: none; cursor: pointer;
  transition: border-color .14s;
}
.rt-pag-size select:focus { border-color: var(--brand, #F26522); }
.rt-pg-btn {
  width: 28px; height: 28px; border-radius: 7px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg, #fff); color: var(--text-2, #6B6B6B);
  font-size: .78rem; font-weight: 600;
  display: grid; place-items: center;
  cursor: pointer; transition: all .14s;
  font-family: var(--font-sans, -apple-system, sans-serif);
}
.rt-pg-btn:hover:not(:disabled) { border-color: var(--brand, #F26522); color: var(--brand, #F26522); background: var(--brand-dim, rgba(242,101,34,.07)); }
.rt-pg-btn:disabled { opacity: .35; cursor: not-allowed; }
.rt-pg-btn.active   { background: var(--brand, #F26522); border-color: var(--brand, #F26522); color: #fff; }
.rt-pg-ellipsis { width: 28px; height: 28px; display: grid; place-items: center; font-size: .78rem; color: var(--text-3, #B4B4B4); }

@media(max-width: 640px) {
  .rt-table-wrap  { display: none; }
  .rt-mobile-grid { display: flex; }
  .rt-pagination  { justify-content: center; }
  .rt-pag-info    { width: 100%; text-align: center; }
}
`;

const getTablePublicUrl = (qrData, business) => {
    const publicCode = qrData?.public_access_code;
    if (!publicCode) return qrData?.public_url;

    const cleanDomain = String(business?.domain || '').trim()
        .replace(/^https?:\/\//i, '')
        .replace(/\/+$/, '');
    if (cleanDomain) return `https://${cleanDomain}/table/${publicCode}`;

    const subdomain = String(business?.subdomain || '').trim()
        .replace(/^https?:\/\//i, '')
        .replace(/[^a-zA-Z0-9-]/g, '');
    if (subdomain) return `https://ecommerce.smartpay.${subdomain}.ai/table/${publicCode}`;

    return qrData?.public_url;
};

/* ── ICONS ── */
const IcoGrid = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>;
const IcoTable = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18" /></svg>;
const IcoPlus = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IcoAssign = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>;
const IcoBranch = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
const IcoChevL = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>;
const IcoChevR = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>;
const IcoChevsL = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>;
const IcoChevsR = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>;

/* ════════════════════════════════════════════════════════
   COMPONENT — lógica 100% original
════════════════════════════════════════════════════════ */
const RoomsTables = () => {

    /* inject CSS once */
    useEffect(() => {
        const id = "rt-styles";
        if (!document.getElementById(id)) {
            const el = document.createElement("style");
            el.id = id; el.textContent = RT_CSS;
            document.head.appendChild(el);
        }
    }, []);

    /* ════════════════════════════════════
       LÓGICA ORIGINAL — 100% INTACTA
    ════════════════════════════════════ */
    let { business_code } = useParams();
    let user = JSON.parse(localStorage.getItem('user'));
    let token = localStorage.getItem('x-access-token');

    const { selectedBranch, setSelectedBranch } = useSelectedBranch();
    const [userBranchoffices, setUserBranchs] = useState([]);
    const [eventHandler, setEventHandler] = useState(0);
    const [tablesList, setTablesList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editShowModal, setEditShowModal] = useState(false);
    const [businessClients, setBusinessClients] = useState([]);
    const [showTableQrModal, setShowTableQrModal] = useState(false);
    const [tableQrData, setTableQrData] = useState(null);
    const [tableQrLoading, setTableQrLoading] = useState(false);
    const [tableForQr, setTableForQr] = useState(null);

    // Pre-pago config (Fase 6) - prepay_tables_enabled/prepay_name_mode, both plain branchoffice
    // columns (see services/branchoffice.js on the backend). getBranchOfficeByCode already returns
    // every column (SELECT *), so this is populated straight from that same call - no extra fetch.
    const [showPrepaySettingsModal, setShowPrepaySettingsModal] = useState(false);
    const [prepaySettings, setPrepaySettings] = useState({ prepay_tables_enabled: false, prepay_name_mode: 'REQUIRED' });
    const [savingPrepaySettings, setSavingPrepaySettings] = useState(false);

    const [modalFields, setModalFields] = useState([
        { name: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese nombre', required: true },
        { name: 'client', label: 'Cliente', type: 'select', options: [], required: false },
    ]);
    const [modalFieldsNewTable, setModalFieldsNewTable] = useState([
        { name: 'name', label: 'Nombre', type: 'text', placeholder: 'Ingrese nombre', required: true },
    ]);
    const [formData, setFormData] = useState({});
    const [selectedTable, setSelectedTable] = useState({
        name: "", code: "", password: "", client: 0, status: 0, open_order: 0, id: 0
    });

    const [environmentsList, setEnvironmentsList] = useState([]);
    const [selectedEnvironment, setSelectedEnvironment] = useState(null);
    const [selectedEnvTableIds, setSelectedEnvTableIds] = useState([]);
    const [showEnvModal, setShowEnvModal] = useState(false);
    const [showAssignTablesModal, setShowAssignTablesModal] = useState(false);
    const [assignSelection, setAssignSelection] = useState([]);
    const [envFormData, setEnvFormData] = useState({});

    const modalFieldsNewEnv = [
        { name: 'name', label: 'Nombre', type: 'text', placeholder: 'Ej: Terraza', required: true },
    ];

    const availableTablesToAssign = tablesList.filter(t => !selectedEnvTableIds.includes(t.id));
    const filteredTables = selectedEnvironment?.id
        ? tablesList.filter(t => selectedEnvTableIds.includes(t.id))
        : tablesList;

    const actionSelectBranch = (branchCode) => { getBranchoffice(branchCode); };

    const getBranchoffice = async (code) => {
        try {
            const branch = await BranchOfficeFetchAPI.getBranchOfficeByCode(business_code, code);
            setSelectedBranch({ name: branch.data.branchoffice.name, code: branch.data.branchoffice.code, id: branch.data.branchoffice.id });
            setPrepaySettings({
                prepay_tables_enabled: Boolean(branch.data.branchoffice.prepay_tables_enabled),
                prepay_name_mode: branch.data.branchoffice.prepay_name_mode || 'REQUIRED',
            });
            setEventHandler(1);
        } catch (err) {
            setSelectedBranch({ code: 0, id: 0 });
            setEventHandler(1);
        }
    };

    const savePrepaySettings = async (values) => {
        if (!selectedBranch?.id || savingPrepaySettings) return;
        setSavingPrepaySettings(true);
        try {
            await BranchOfficeFetchAPI.updateBranchoffice(selectedBranch.id, values, token);
            setPrepaySettings(values);
            setShowPrepaySettingsModal(false);
            ErrorAlert("Configuracion guardada", "La configuracion de prepago fue actualizada correctamente", "success");
        } catch (err) {
            ErrorAlert("Error", "No se pudo guardar la configuracion de prepago", "error");
        } finally {
            setSavingPrepaySettings(false);
        }
    };

    const getEnvironments = async (branchoffice_id) => {
        try {
            const resp = await BranchOfficeEnvironmentsFetchAPI.getBranchofficeEnvironments(branchoffice_id, token);
            const envs = resp?.data?.environments || resp?.data?.data || [];
            setEnvironmentsList(envs);
            setSelectedEnvironment(null);
            setSelectedEnvTableIds([]);
        } catch (err) {
            setEnvironmentsList([]);
            setSelectedEnvironment(null);
            setSelectedEnvTableIds([]);
        }
    };

    const onSelectEnvironment = async (env) => {
        try {
            setSelectedEnvironment(env);
            setSelectedEnvTableIds([]);
            const resp = await BranchOfficeEnvironmentsFetchAPI.getBranchofficeEnvironmentDetails(env.id, token);
            const details = resp?.data?.environment;
            const ids = (details?.tables || []).map(t => t.table_id);
            setSelectedEnvTableIds(ids);
            setSelectedEnvironment(details);
        } catch (err) { setSelectedEnvTableIds([]); }
    };

    const getTables = async (branchoffice_id) => {
        try {
            const tables = await TablesFetchAPI.getBranchofficeTables(branchoffice_id, token);
            console.log(tables);
            setTablesList(tables.data.tables);
        } catch (err) { setTablesList([]); }
    };

    const getClients = async () => {
        try {
            const clients = await ClientsFetchAPI.getClientsByBusiness(user.business_id, token);
            let clientsList = clients.data.data.map(client => ({ value: client.id, label: client.name }));
            let modalFieldsCopy = [...modalFields];
            modalFieldsCopy[1].options = clientsList;
            modalFieldsCopy[1].options.unshift({ value: 0, label: "Seleccione cliente" });
            setModalFields(modalFieldsCopy);
            setBusinessClients(modalFieldsCopy[1].options);
        } catch (err) {
            let modalFieldsCopy = [...modalFields];
            modalFieldsCopy[1].options = [];
            setModalFields(modalFieldsCopy);
            setBusinessClients([]);
        }
    };

    const getTableDetails = async (table_id, modal_type) => {
        try {
            const table = await TablesFetchAPI.getTableDetails(table_id, token);
            console.log(table);
            setSelectedTable({
                name: table.data.table.name, code: table.data.table.code,
                password: table.data.table.password,
                client: table.data.table.client_id !== null ? table.data.table.client_id : 0,
                status: table.data.table.status, open_order: table.data.table.open_order, id: table_id
            });
            if (modal_type === "edit") {
                let lastModalFields = [...modalFields];
                lastModalFields[0].value = table.data.table.name;
                lastModalFields[1].value = table.data.table.client_id !== null ? table.data.table.client_id : 0;
                lastModalFields[1].options = businessClients;
                lastModalFields[1].defaultValue = 0;
                setModalFields(lastModalFields);
                setFormData({ name: table.data.table.name, client: 0 });
                setEventHandler(1);
            }
        } catch (err) { console.log(err); }
    };

    const createEnvironment = async (e) => {
        e.preventDefault();
        try {
            const payload = { branchoffice_id: selectedBranch.id, ...envFormData };
            await BranchOfficeEnvironmentsFetchAPI.createBranchofficeEnvironment(payload, selectedBranch.id, token);
            ErrorAlert("Ambiente creado", "El ambiente fue creado correctamente", "success");
            setShowEnvModal(false);
            setEnvFormData({});
            setEventHandler(1);
        } catch (err) { ErrorAlert("Error", "No se pudo crear el ambiente", "error"); }
    };

    const openAssignTablesSwal = async () => {
        if (!selectedEnvironment?.id) return;
        const html = `
      <div style="text-align:left; max-height: 300px; overflow:auto;">
        ${availableTablesToAssign.map(t => `
          <div style="display:flex; gap:8px; align-items:center; margin-bottom:6px;">
            <input type="checkbox" class="swal-table-checkbox" value="${t.id}" id="tbl_${t.id}" />
            <label for="tbl_${t.id}">${t.name}</label>
          </div>
        `).join("")}
      </div>
    `;
        const result = await Swal.fire({
            title: 'Asignar mesas', html,
            showCancelButton: true, confirmButtonText: 'Asignar', cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const checks = Array.from(document.querySelectorAll('.swal-table-checkbox:checked'));
                return checks.map(x => Number(x.value));
            }
        });
        if (!result.isConfirmed) return;
        const selectedIds = result.value || [];
        if (selectedIds.length === 0) { ErrorAlert("Atención", "Seleccione al menos una mesa", "warning"); return; }
        try {
            const payload = { branchoffice_environment_id: selectedEnvironment.id, table_ids: selectedIds };
            await BranchOfficeEnvironmentsFetchAPI.assignTablesToEnvironment(payload, selectedEnvironment.id, token);
            ErrorAlert("Mesas asignadas", "Se asignaron correctamente", "success");
            await onSelectEnvironment({ id: selectedEnvironment.id });
            setEventHandler(1);
        } catch (err) { ErrorAlert("Error", "No se pudieron asignar las mesas", "error"); }
    };

    const assignTablesToEnvironment = async (e) => {
        e.preventDefault();
        if (!selectedEnvironment?.id) return;
        try {
            const payload = { branchoffice_environment_id: selectedEnvironment.id, table_ids: assignSelection };
            await BranchOfficeEnvironmentsFetchAPI.assignTablesToEnvironment(payload, token);
            ErrorAlert("Mesas asignadas", "Se asignaron correctamente", "success");
            setShowAssignTablesModal(false);
            setAssignSelection([]);
            setEventHandler(1);
        } catch (err) { ErrorAlert("Error", "No se pudieron asignar las mesas", "error"); }
    };

    const removeTableFromEnvironment = async (table_id) => {
        if (!selectedEnvironment?.id) return;
        try {
            const payload = { branchoffice_environment_id: selectedEnvironment.id, table_ids: [table_id] };
            await BranchOfficeEnvironmentsFetchAPI.removeTablesFromEnvironment(payload, token);
            ErrorAlert("Mesa removida", "Se removió del ambiente correctamente", "success");
            setEventHandler(1);
        } catch (err) { ErrorAlert("Error", "No se pudo remover la mesa del ambiente", "error"); }
    };

    const editUser = async (e) => {
        e.preventDefault();
        try {
            const response = await TablesFetchAPI.updateTable(selectedTable.id, formData, token);
            setEventHandler(1);
            ErrorAlert("Usuario editado", "El usuario fue editado correctamente", "success");
            setEditShowModal(false);
        } catch (err) {
            console.log(err);
            ErrorAlert("Error al editar usuario", "No se pudo editar el usuario, intente de nuevo", "error");
        }
    };

    const removeTable = async (e) => {
        let table_id = e.currentTarget.dataset.id;
        try {
            Swal.fire({
                title: '¿Está seguro?', text: "No podrá revertir esta acción!", icon: 'warning',
                showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33',
                confirmButtonText: 'Si, eliminar!', cancelButtonText: 'Cancelar'
            }).then(async (result) => {
                if (result) {
                    await TablesFetchAPI.deleteTable(table_id, token);
                    setEventHandler(1);
                    ErrorAlert("Mesa eliminada", "La mesa fue eliminada correctamente", "success");
                }
            });
        } catch (err) {
            console.log(err);
            ErrorAlert("Error al eliminar mesa", "No se pudo eliminar la mesa, intente de nuevo", "error");
        }
    };

    const generatePassword = async (e) => {
        let password = generateRandomString(8);
        let data = { table_password: password };
        try {
            await TablesFetchAPI.updateTable(e.currentTarget.dataset.id, data, token);
            setEventHandler(1);
        } catch (err) { ErrorAlert(err.response.data.error); }
    };

    const generateTableQr = async (table) => {
        setTableForQr(table);
        setTableQrData(null);
        setTableQrLoading(true);
        setShowTableQrModal(true);
        try {
            const response = await TableSessionsFetchAPI.getTableQr(table.id, token);
            const qrData = response.data?.data || response.data;
            setTableQrData({
                ...qrData,
                public_url: getTablePublicUrl(qrData, user.business),
            });
        } catch (err) {
            setShowTableQrModal(false);
            ErrorAlert('Error al generar QR', err.response?.data?.error || 'No se pudo generar el acceso publico de la mesa.', 'error');
        } finally {
            setTableQrLoading(false);
        }
    };

    const newTable = async (e) => {
        e.preventDefault();
        try {
            formData.branchoffice_id = selectedBranch.id;
            const response = await TablesFetchAPI.createTable(formData, token);
            setEventHandler(1);
            ErrorAlert("Mesa creada", "La mesa fue creada correctamente", "success");
            setShowModal(false);
        } catch (err) {
            console.log(err);
            ErrorAlert("Error al crear mesa", "No se pudo crear la mesa, intente de nuevo", "error");
        }
    };

    const handleSwitchChange = async (e) => {
        let data = { status: e.target.checked ? 1 : 0 };
        try {
            await TablesFetchAPI.updateTable(e.target.id, data, token);
            setEventHandler(1);
        } catch (err) {
            ErrorAlert(err.response.data.error);
            e.target.checked = false;
            setEventHandler(1);
        }
    };

    const handleEnvFieldChange = (fieldName, value) => {
        setEnvFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    const handleFieldChange = (fieldName, value) => {
        setFormData(prevData => ({ ...prevData, [fieldName]: value }));
    };

    useEffect(() => {
        setUserBranchs(user.branchoffices);
        getClients();
        setEventHandler(0);
    }, []);

    useEffect(() => {
        if (selectedBranch.code !== 0) {
            getTables(selectedBranch.id);
            getEnvironments(selectedBranch.id);
        }
        setSelectedEnvironment(null);
        setSelectedEnvTableIds([]);
        setFormData({});
        setEventHandler(0);
    }, [eventHandler]);
    /* ════════════════════════════════════
       FIN LÓGICA ORIGINAL
    ════════════════════════════════════ */

    /* ── UI state: paginación ── */
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => { setCurrentPage(1); }, [selectedEnvironment, pageSize, eventHandler]);

    const totalItems = filteredTables.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const paginated = filteredTables.slice((safePage - 1) * pageSize, safePage * pageSize);

    const pageNumbers = (() => {
        if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (safePage <= 3) return [1, 2, 3, 4, '…', totalPages];
        if (safePage >= totalPages - 2) return [1, '…', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, '…', safePage - 1, safePage, safePage + 1, '…', totalPages];
    })();

    const isHotel = user.business.business_type.panamanianCode !== "9010";
    const entityLabel = isHotel ? "Habitaciones" : "Mesas";

    /* ═══════════════════════════════════
       RENDER
    ═══════════════════════════════════ */
    return (
        <>

            <AppShell route="tables" >
                <MainContent>

                    <div className="rt-page">

                        {/* ── BANNER ── */}
                        <div className="rt-banner">
                            <div className="rt-banner-left">
                                <p className="rt-banner-eyebrow">Gestión</p>
                                <h1 className="rt-banner-title">
                                    {isHotel ? "Gestión de habitaciones" : "Gestión de mesas"}
                                    {selectedBranch.name && (
                                        <span className="rt-banner-branch">— {selectedBranch.name}</span>
                                    )}
                                </h1>
                                <p className="rt-banner-sub">
                                    {selectedBranch.name ? `${totalItems} ${entityLabel.toLowerCase()} · ${environmentsList.length} ambientes` : "Selecciona una sucursal para comenzar"}
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <RenderIf isTrue={selectedBranch.code !== 0}>
                                    <button
                                        className="rt-btn"
                                        type="button"
                                        onClick={() => setShowPrepaySettingsModal(true)}
                                        title="Configuracion de pedido en mesa con pago anticipado"
                                    >
                                        Prepago
                                    </button>
                                </RenderIf>
                                <ModalBranchofficeSelect actionSelectBranch={actionSelectBranch} />
                            </div>
                        </div>

                        {/* ── SIN SUCURSAL ── */}
                        {selectedBranch.code === 0 && (
                            <div className="rt-no-branch">
                                <div className="rt-no-branch-icon"><IcoBranch /></div>
                                <p className="rt-no-branch-title">Ninguna sucursal seleccionada</p>
                                <p className="rt-no-branch-sub">Selecciona una sucursal en la barra superior para ver y gestionar {entityLabel.toLowerCase()}.</p>
                            </div>
                        )}

                        {/* ── CONTENIDO PRINCIPAL ── */}
                        <RenderIf isTrue={selectedBranch.code !== 0}>
                            <div className="rt-grid">

                                {/* ── COL AMBIENTES ── */}
                                <div className="rt-card">
                                    <div className="rt-card-head">
                                        <span className="rt-card-title">
                                            <IcoGrid /> Ambientes
                                            <span className="rt-count-badge">{environmentsList.length}</span>
                                        </span>
                                        <button className="rt-btn" onClick={() => setShowEnvModal(true)}>
                                            <IcoPlus /> Crear
                                        </button>
                                    </div>

                                    <div className="rt-env-list">
                                        {/* opción "Todos" */}
                                        <button
                                            className={`rt-env-item${!selectedEnvironment?.id ? ' active' : ''}`}
                                            onClick={() => { setSelectedEnvironment(null); setSelectedEnvTableIds([]); }}
                                        >
                                            <span className="rt-env-dot" />
                                            Todos
                                        </button>

                                        {environmentsList.length === 0 ? (
                                            <p className="rt-env-empty">No hay ambientes creados.</p>
                                        ) : environmentsList.map(env => (
                                            <button
                                                key={env.id}
                                                className={`rt-env-item${selectedEnvironment?.id === env.id ? ' active' : ''}`}
                                                onClick={() => onSelectEnvironment(env)}
                                            >
                                                <span className="rt-env-dot" />
                                                {env.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* ── COL MESAS/HABITACIONES ── */}
                                <div>
                                    <div className="rt-card">
                                        <div className="rt-tables-head">
                                            <span className="rt-card-title">
                                                <IcoTable />
                                                {selectedEnvironment?.name
                                                    ? `${entityLabel} en ${selectedEnvironment.name}`
                                                    : `Todas las ${entityLabel}`}
                                                <span className="rt-count-badge">{totalItems}</span>
                                            </span>
                                            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                                                {selectedEnvironment?.id && (
                                                    <button className="rt-btn-ghost" onClick={openAssignTablesSwal}>
                                                        <IcoAssign /> Asignar {entityLabel.toLowerCase()}
                                                    </button>
                                                )}
                                                <FormSmartpayModal
                                                    buttonModal={true}
                                                    button_text={`Agregar ${isHotel ? 'habitación' : 'mesa'}`}
                                                    modal_size={"lg"}
                                                    modal_header_text={`Agregar ${isHotel ? 'habitación' : 'mesa'}`}
                                                    modal_body={modalFieldsNewTable}
                                                    submit={newTable}
                                                    setShowModal={setShowModal}
                                                    showModal={showModal}
                                                    handleFieldChange={handleFieldChange}
                                                    initialFormValues={formData}
                                                    formType={"addForm"}
                                                    setEventHandler={setEventHandler}
                                                />
                                            </div>
                                        </div>

                                        {/* ── TABLA DESKTOP ── */}
                                        <div className="rt-table-wrap">
                                            {filteredTables.length === 0 ? (
                                                <div className="rt-empty">
                                                    <div className="rt-empty-icon">🪑</div>
                                                    <p className="rt-empty-title">Sin {entityLabel.toLowerCase()}</p>
                                                    <p className="rt-empty-sub">Agrega {entityLabel.toLowerCase()} con el botón de arriba.</p>
                                                </div>
                                            ) : (
                                                <Table
                                                    title={entityLabel}
                                                    table_headers={["", "Estados", "Nombre", "Codigo", "Contraseña", "Cliente", "Acciones"]}
                                                    table_data={paginated}
                                                    table_type={"rooms_table"}
                                                    user={user}
                                                    table_name={entityLabel}
                                                    setEditShowModal={setEditShowModal}
                                                    getTableDetails={getTableDetails}
                                                    handleSwitchChange={handleSwitchChange}
                                                    generatePassword={generatePassword}
                                                    generateTableQr={generateTableQr}
                                                    removeData={removeTable}
                                                    removeFromEnvironment={removeTableFromEnvironment}
                                                    selectedEnvironment={selectedEnvironment}
                                                />
                                            )}
                                        </div>

                                        {/* ── CARDS MOBILE ── */}
                                        <div className="rt-mobile-grid">
                                            {filteredTables.length === 0 ? (
                                                <div className="rt-empty">
                                                    <div className="rt-empty-icon">🪑</div>
                                                    <p className="rt-empty-title">Sin {entityLabel.toLowerCase()}</p>
                                                    <p className="rt-empty-sub">Agrega {entityLabel.toLowerCase()} con el botón de arriba.</p>
                                                </div>
                                            ) : paginated.map((table, i) => (
                                                <div
                                                    className="rt-table-card"
                                                    key={table.id}
                                                    style={{ animationDelay: `${i * 0.04}s` }}
                                                >
                                                    <div className="rt-table-card-icon">🪑</div>
                                                    <div className="rt-table-card-body">
                                                        <div className="rt-table-card-name">{table.name}</div>
                                                        <div className="rt-table-card-code">#{table.code}</div>
                                                    </div>
                                                    <div className="rt-table-card-right">
                                                        <span className={`rt-status-dot ${table.status === 1 ? 'rt-status-open' : 'rt-status-closed'}`}>
                                                            {table.status === 1 ? 'Activa' : 'Inactiva'}
                                                        </span>
                                                        <button
                                                            className="rt-btn-ghost"
                                                            style={{ fontSize: '.72rem', padding: '.28rem .6rem' }}
                                                            onClick={() => { getTableDetails(table.id, "edit"); setEditShowModal(true); }}
                                                        >
                                                            Editar
                                                        </button>
                                                        <button
                                                            className="rt-btn-ghost"
                                                            style={{ fontSize: '.72rem', padding: '.28rem .6rem' }}
                                                            onClick={() => generateTableQr(table)}
                                                        >
                                                            QR / NFC
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                 

                                    </div>
                                </div>

                            </div>
                        </RenderIf>

                        {/* ── MODALES ORIGINALES ── */}
                        <FormSmartpayModal
                            buttonModal={false}
                            modal_size={"lg"}
                            modal_header_text={"Editar usuario"}
                            modal_body={modalFields}
                            submit={editUser}
                            setShowModal={setEditShowModal}
                            showModal={editShowModal}
                            handleFieldChange={handleFieldChange}
                            initialFormValues={formData}
                            formType={"editForm"}
                        />
                        <FormSmartpayModal
                            buttonModal={false}
                            modal_size={"md"}
                            modal_header_text={"Crear ambiente"}
                            modal_body={modalFieldsNewEnv}
                            submit={createEnvironment}
                            setShowModal={setShowEnvModal}
                            showModal={showEnvModal}
                            handleFieldChange={handleEnvFieldChange}
                            initialFormValues={envFormData}
                            formType={"addForm"}
                        />
                        <TableAccessQrModal
                            show={showTableQrModal}
                            onHide={() => setShowTableQrModal(false)}
                            table={tableForQr}
                            qrData={tableQrData}
                            loading={tableQrLoading}
                        />
                        <PrepaySettingsModal
                            show={showPrepaySettingsModal}
                            onHide={() => setShowPrepaySettingsModal(false)}
                            initialValues={prepaySettings}
                            onSave={savePrepaySettings}
                            saving={savingPrepaySettings}
                        />

                    </div>

                </MainContent>

            </AppShell>

        </>
    );
};

export default RoomsTables;
